import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, Trash2 } from "lucide-react";
import api from "@/api";

export default function AdjustmentsModal({ residuo, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tipo, setTipo] = useState("ferie");
  const [delta, setDelta] = useState(0);
  const [nota, setNota] = useState("");
  const [riportoFerie, setRiportoFerie] = useState(residuo.giorni_ferie_riporto || 0);
  const [riportoPermesso, setRiportoPermesso] = useState(residuo.ore_permesso_riporto || 0);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/ferie/aggiustamenti", {
        params: { lavoratore_id: residuo.lavoratore_id, anno: residuo.anno },
      });
      setItems(r.data || []);
    } catch (e) {
      toast.error("Impossibile caricare aggiustamenti");
    } finally {
      setLoading(false);
    }
  }, [residuo.lavoratore_id, residuo.anno]);

  useEffect(() => {
    reload();
  }, [reload]);

  const addAdj = async () => {
    if (!delta || delta === 0) {
      toast.error("Delta non può essere 0");
      return;
    }
    setSaving(true);
    try {
      await api.post("/ferie/aggiustamenti", {
        lavoratore_id: residuo.lavoratore_id,
        anno: residuo.anno,
        tipo,
        delta: parseFloat(delta),
        nota,
      });
      setDelta(0);
      setNota("");
      toast.success("Aggiustamento salvato");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const removeAdj = async (id) => {
    if (!window.confirm("Rimuovere questo aggiustamento?")) return;
    try {
      await api.delete(`/ferie/aggiustamenti/${id}`);
      toast.success("Aggiustamento rimosso");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const saveRiporto = async () => {
    setSaving(true);
    try {
      await api.patch(`/lavoratori/${residuo.lavoratore_id}`, {
        riporto_ferie_gg: parseFloat(riportoFerie) || 0,
        riporto_permesso_ore: parseFloat(riportoPermesso) || 0,
      });
      toast.success("Riporto aggiornato");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="adjustments-modal"
    >
      <div
        className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">
              Modifica saldo · {residuo.lavoratore_nome || residuo.lavoratore_id}
            </h2>
            <p className="text-xs text-slate-500">
              Anno {residuo.anno}. Gli aggiustamenti si sommano al calcolo automatico e restano
              nello storico.
            </p>
          </div>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* RIPORTO */}
          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
              Riporto anno precedente
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Ferie riportate (gg)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={riportoFerie}
                  onChange={(e) => setRiportoFerie(e.target.value)}
                  data-testid="input-riporto-ferie"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Permessi riportati (ore)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={riportoPermesso}
                  onChange={(e) => setRiportoPermesso(e.target.value)}
                  data-testid="input-riporto-permesso"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                />
              </div>
            </div>
            <button
              onClick={saveRiporto}
              disabled={saving}
              data-testid="btn-save-riporto"
              className="mt-2 px-3 py-1.5 text-xs font-semibold bg-slate-800 hover:bg-slate-900 text-white rounded-sm disabled:opacity-60"
            >
              Salva riporto
            </button>
          </section>

          {/* AGGIUSTAMENTI */}
          <section>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
              Aggiustamenti manuali
            </div>
            <div className="grid grid-cols-[1fr_120px_140px_1fr_auto] gap-2 items-end mb-3">
              <div>
                <label className="text-[10px] font-semibold text-slate-600 block mb-1">Tipo</label>
                <select
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  data-testid="adj-tipo"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm bg-white"
                >
                  <option value="ferie">Ferie (gg)</option>
                  <option value="permesso">Permessi (ore)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-600 block mb-1">Delta</label>
                <input
                  type="number"
                  step="0.5"
                  value={delta}
                  onChange={(e) => setDelta(e.target.value)}
                  data-testid="adj-delta"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm font-mono"
                  placeholder="+2 o -1"
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] font-semibold text-slate-600 block mb-1">Nota</label>
                <input
                  type="text"
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  data-testid="adj-nota"
                  placeholder="Es. Bonus fedeltà"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <button
                onClick={addAdj}
                disabled={saving}
                data-testid="btn-add-adj"
                className="px-3 py-1.5 text-sm font-semibold bg-blue-700 hover:bg-blue-800 text-white rounded-sm disabled:opacity-60"
              >
                Aggiungi
              </button>
            </div>

            {loading ? (
              <div className="text-sm text-slate-500 py-4 text-center">Caricamento…</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-slate-400 py-4 text-center">
                Nessun aggiustamento manuale per l'anno {residuo.anno}
              </div>
            ) : (
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-600">
                    <tr>
                      <th className="text-left p-2">Data</th>
                      <th className="text-left p-2">Tipo</th>
                      <th className="text-right p-2">Delta</th>
                      <th className="text-left p-2">Nota</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((a) => (
                      <tr key={a.aggiustamento_id} className="border-t border-slate-100">
                        <td className="p-2 text-xs">
                          {(a.created_at || "").slice(0, 10)}
                        </td>
                        <td className="p-2 text-xs">
                          {a.tipo === "ferie" ? "Ferie (gg)" : "Permessi (h)"}
                        </td>
                        <td
                          className={`p-2 text-right font-mono font-bold ${
                            a.delta >= 0 ? "text-emerald-700" : "text-rose-700"
                          }`}
                        >
                          {a.delta >= 0 ? "+" : ""}
                          {a.delta}
                        </td>
                        <td className="p-2 text-xs">{a.nota || "—"}</td>
                        <td className="p-2 text-right">
                          <button
                            onClick={() => removeAdj(a.aggiustamento_id)}
                            className="text-rose-600 hover:text-rose-800"
                            title="Rimuovi"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50 sticky bottom-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-white"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
